import { createContext, useEffect, useMemo, useState } from "react";
import { auth } from "../Chat/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../Config/firebase";
import { getCommunityByCommunityCode } from "../Config/db/community.retrieve";

export const AuthContext = createContext();


export const AuthContextProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState({});
  const [userInfo, setUserInfo] = useState(null);
  const [totalFlats, setTotalFlats] = useState(0);
  const [communityFlats, setCommunityFlats] = useState([]);
  const [memberCount, setMemberCount] = useState(0);

  const userSessionPatch = (user) => {
    setCurrentUser(user);
    if (user) {
      getDoc(doc(db, "users", user.uid))
        .then(async (docSnap) => {
          let userData = docSnap.data();
          let resolvedUid = user.uid;

          // If no user doc by UID, check the authMappings table
          if (!userData) {
            const mappingSnap = await getDoc(doc(db, "authMappings", user.uid));
            if (mappingSnap.exists()) {
              const { originalUid } = mappingSnap.data();
              const origSnap = await getDoc(doc(db, "users", originalUid));
              if (origSnap.exists()) {
                userData = origSnap.data();
                resolvedUid = originalUid;
              }
            }
          }

          if (!userData) {
            setUserInfo(null);
            return;
          }
          // Fetch representators for this user to get flats
          const { getDocs, collection, query, where } = await import("firebase/firestore");
          const repsSnap = await getDocs(
            query(
              collection(db, "representators"),
              where("userId", "==", resolvedUid),
              where("active", "==", true),
              where("retired", "==", false)
            )
          );
          const reps = repsSnap.docs.map(d => d.data());
          const flats = reps.filter(r => r.flatId).map(r => r.flatId);
          const isUnassigned = flats.length === 0;
          const communityHistory = userData.communityHistory || [];
          const rejoinRequested = userData.rejoinRequested || false;
          setUserInfo({ ...userData, flats, isUnassigned, communityHistory, rejoinRequested });
          if (userData?.communityCode) {
            getCommunityByCommunityCode(userData.communityCode).then(
              (data) => {
                const allFlats = data?.flat || [];
                setTotalFlats(allFlats.length);
                setCommunityFlats(allFlats);
              }
            ).catch(() => {});
            // Count active members
            getDocs(
              query(
                collection(db, "representators"),
                where("communityCode", "==", userData.communityCode),
                where("active", "==", true),
                where("retired", "==", false)
              )
            ).then((snap) => setMemberCount(snap.size)).catch(() => {});
          }
        })
        .catch(() => {});
    }
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      userSessionPatch(user);
    });

    return () => {
      unsub();
    };
  }, []);

  const contextValue = useMemo(() => ({
    totalFlats,
    communityFlats,
    memberCount,
    currentUser,
    userInfo,
    setUserInfo,
    userSessionPatch,
  }), [totalFlats, communityFlats, memberCount, currentUser, userInfo]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
